/**
 * Role-based permission configuration for Besyildiz customer panel.
 *
 * This file defines the mapping between role names and allowed reports.
 * Each customer panel can have its own rolePermissions.ts with different
 * role-permission mappings tailored to their specific needs.
 *
 * Role names must match exactly what's defined in the Admin Panel for this firm.
 * Report keys must match the route paths defined in the application.
 */

// Role-based permission mapping
// Key: Role name (must match Admin Panel role names exactly)
// Value: Array of allowed report keys ('*' means all reports)
export const ROLE_PERMISSIONS: Record<string, string[]> = {
  // Admin role - full access to all reports
  'Admin': ['*'],

  // Muhasebe (Accounting) role - financial reports
  'Muhasebe': [
    'dashboard',
    'kasa-raporu',
    'bankalar',
    'alacaklilar',
    'borclular',
    'verilen-cekler',
    'alinan-cekler',
    'giderler-raporu',
  ],

  // Satis (Sales) role - sales-related reports
  'Satis': [
    'dashboard',
    'ciro-takip',
    'satis-karlilik',
    'fire-raporu',
    'stok-fifo',
    'stok-maliyet',
  ],

  // Satış (Sales with Turkish character) - alias
  'Satış': [
    'dashboard',
    'ciro-takip',
    'satis-karlilik',
    'fire-raporu',
    'stok-fifo',
    'stok-maliyet',
  ],

  // Depo (Warehouse) role - inventory reports
  'Depo': [
    'dashboard',
    'stok-fifo',
    'stok-maliyet',
    'fire-raporu',
  ],

  // Kullanıcı (Standard User) role - basic access
  'Kullanıcı': [
    'dashboard',
    'ciro-takip',
  ],
};

// Default permissions for undefined roles
export const DEFAULT_PERMISSIONS: string[] = ['dashboard'];

/**
 * Get the list of allowed report keys for a given role name.
 *
 * @param roleName - The name of the role (e.g., 'Admin', 'Muhasebe')
 * @returns Array of allowed report keys, or DEFAULT_PERMISSIONS if role is not found
 */
export function getPermissionsForRole(roleName: string | undefined | null): string[] {
  if (!roleName) return DEFAULT_PERMISSIONS;
  return ROLE_PERMISSIONS[roleName] || DEFAULT_PERMISSIONS;
}

/**
 * Check if a user with the given role has access to a specific report.
 *
 * @param roleName - The name of the user's role
 * @param reportKey - The report key to check access for (e.g., 'kasa-raporu')
 * @returns true if the user has access to the report
 */
export function hasPermission(roleName: string | undefined | null, reportKey: string): boolean {
  const permissions = getPermissionsForRole(roleName);

  // Wildcard '*' grants access to all reports
  if (permissions.includes('*')) return true;

  return permissions.includes(reportKey);
}

/**
 * Get all available report keys that a role has access to.
 * Useful for filtering menu items.
 *
 * @param roleName - The name of the user's role
 * @param allReportKeys - List of all available report keys in the system
 * @returns Filtered list of report keys the role can access
 */
export function getAccessibleReports(
  roleName: string | undefined | null,
  allReportKeys: string[]
): string[] {
  const permissions = getPermissionsForRole(roleName);

  // Wildcard means all reports are accessible
  if (permissions.includes('*')) return allReportKeys;

  return allReportKeys.filter(key => permissions.includes(key));
}
